// `/live-stream` — the Manager-facing broadcast console (feature-flagged).
// A thin primary adapter (ADR 0005): it registers the path, gates on the
// `live-stream` flag in `beforeLoad` (the rule lives in a unit-testable guard),
// pre-warms the config cache the surface gates on, reads the tenant scope from
// the parent `_authenticated` context, and delegates the UI to
// `BroadcastConsolePage` in the streaming context's `ui/` layer.

import { createFileRoute, useRouteContext } from '@tanstack/react-router';
import { useState } from 'react';
import { featureFlagsQueryKey } from '~/_shared/feature-flags/evaluate-flags';
import { evaluateFeatureFlagsServerFn } from '~/_shared/feature-flags/evaluate-flags.fn';
import { gateLiveStreamRoute } from '~/bounded-contexts/streaming/application/client/live-stream-route-guard';
import { streamConfigQueryKey } from '~/bounded-contexts/streaming/application/client/query-key';
import { getConfigServerFn } from '~/bounded-contexts/streaming/application/get-config.fn';
import { BroadcastConsolePage } from '~/bounded-contexts/streaming/ui/broadcast-console/broadcast-console-page';
import { BroadcastNavGuard } from '~/bounded-contexts/streaming/ui/broadcast-console/broadcast-nav-guard';

function LiveStreamRouteComponent() {
	const ctx = useRouteContext({ from: '/_authenticated' });
	// The page is router-free; it lifts its attachment state here (a callback
	// prop), and this route — the router-aware layer — arms the in-app
	// navigation guard with it. Armed only while THIS tab publishes (live or
	// paused); the idle console navigates freely.
	const [is_broadcasting, set_is_broadcasting] = useState(false);
	return (
		<>
			<BroadcastNavGuard when={is_broadcasting} />
			<BroadcastConsolePage
				clubId={ctx?.scope?.club?.id ?? ''}
				clubName={ctx?.scope?.club?.name ?? ''}
				on_attachment_change={set_is_broadcasting}
			/>
		</>
	);
}

export const Route = createFileRoute('/_authenticated/live-stream')({
	// Decision 3, PRINCIPLES §3 default-deny: nav-hiding is NOT a gate — the URL
	// is reachable directly, and `provision()` creates a BILLABLE Cloudflare Live
	// Input. The rule moved to `live-stream-route-guard.ts`, where a unit test can
	// reach it (this file is glob-excluded from coverage). The gate BLOCKS: it
	// awaits the flags query (warm cache = synchronous; a cold one pays one eval)
	// before asserting, so the default-deny is enforced before the surface loads.
	// `revalidateIfStale`: without it, `ensureQueryData` returns a cached map and
	// never refetches, so the 60s staleTime is inert and a Provider killing the
	// flag would not bite in a long-lived SPA session. With it the gate still
	// answers from cache (never blocks a warm nav) while a stale map refetches in
	// the background — the kill switch lands on the next nav.
	beforeLoad: ({ context }) =>
		gateLiveStreamRoute(() =>
			context.queryClient.ensureQueryData({
				queryKey: featureFlagsQueryKey(context.scope.club.id),
				queryFn: () => evaluateFeatureFlagsServerFn(),
				staleTime: 60_000,
				revalidateIfStale: true,
			}),
		),
	// Only getConfig is prefetched (decision 4 gates the whole surface on it, so
	// it is on the first-paint path). getStats is NOT — the 10s poll supplies it,
	// and prefetching would fire an extra KV pagination + a debug log per nav.
	// Awaited on the SERVER: a settled query dehydrates, so a fire-and-forget
	// prefetch races dehydration and the client's first render (config present)
	// disagrees with the server HTML (null) — a hydration mismatch. On the CLIENT
	// it's fire-and-forget so a warm nav never blocks; the console renders null
	// for the brief gap until config arrives.
	loader: ({ context }) => {
		const clubId = context.scope.club.id;
		const config = context.queryClient
			.prefetchQuery({
				queryKey: streamConfigQueryKey(clubId),
				queryFn: () => getConfigServerFn(),
			})
			.catch(() => {});
		if (typeof window === 'undefined') return config;
	},
	component: LiveStreamRouteComponent,
});
